// CSV export utilities for converting structured data to CSV format and
// triggering browser downloads. The pure functions (formatCsvValue, tableToCsv)
// live separately from the effectful download function so they can be tested
// without mocking the DOM.

/**
 * Converts a single cell value to a CSV-safe string following RFC 4180.
 *
 * Null and undefined produce empty strings rather than the literal text
 * "null" or "undefined", which would confuse spreadsheet users who expect
 * blank cells for missing data.
 */
export function formatCsvValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringified = typeof value === "string" ? value : String(value);

  // RFC 4180 §2.6: fields containing commas, double quotes, or line
  // breaks must be enclosed in double quotes. We check all three in one
  // pass to avoid redundant string scans.
  const requiresQuoting =
    stringified.includes(",") ||
    stringified.includes('"') ||
    stringified.includes("\n") ||
    stringified.includes("\r");

  if (!requiresQuoting) {
    return stringified;
  }

  // Double every internal quote so parsers don't interpret them as
  // field delimiters, then wrap the whole field in quotes.
  const escaped = stringified.replaceAll('"', '""');
  return `"${escaped}"`;
}

/**
 * Builds a complete CSV string from column names and row data.
 *
 * In naapp, columns are plain strings that serve as both the header
 * label and the data key — unlike the chat project which uses
 * `{ key, label }` objects. This keeps call sites simple when the
 * display name and object key are identical, which is the common case
 * for ag-grid and table exports throughout the app.
 */
export function tableToCsv(
  columns: readonly string[],
  data: readonly Record<string, unknown>[],
): string {
  const headerRow = columns.map((column) => formatCsvValue(column)).join(",");

  const dataRows = data.map((row) =>
    columns.map((column) => formatCsvValue(row[column])).join(","),
  );

  // Bare LF (\n) rather than CRLF — modern spreadsheet applications
  // handle both, and LF avoids doubling line-ending bytes for no
  // practical benefit.
  return [headerRow, ...dataRows].join("\n");
}

/**
 * Triggers a CSV file download in the browser.
 *
 * This is the only effectful function in this module. It uses the
 * temporary-anchor technique because `window.open` with a blob URL is
 * blocked by popup blockers in most browsers, while a programmatic
 * anchor click is not.
 */
export function downloadCsv(csv: string, filename: string): void {
  // Prepend a UTF-8 BOM so Excel recognises the encoding and renders
  // non-ASCII characters (accented names, CJK, emoji) correctly.
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;

  // Appending to the body is required in Firefox — clicking a detached
  // anchor silently does nothing in that browser.
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revoke immediately after the click event is dispatched. The browser
  // captures the blob reference synchronously during click handling, so
  // the download completes even after revocation.
  URL.revokeObjectURL(objectUrl);
}
